import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Box, CircularProgress, Typography, Alert } from "@mui/material";
import { handleCallback } from "../auth/oidcConfig";

function OidcCallback() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in React StrictMode
    if (hasProcessed.current) {
      return;
    }
    hasProcessed.current = true;

    const processCallback = async () => {
      try {
        // Check if this is an error callback (from prompt=none failure)
        const urlParams = new URLSearchParams(window.location.search);
        const errorCode = urlParams.get("error");
        const errorDescription = urlParams.get("error_description");

        if (errorCode) {
          console.log(
            "⚠️ OIDC callback returned error:",
            errorCode,
            errorDescription
          );

          // Handle expected errors from prompt=none auto-login
          if (
            errorCode === "login_required" ||
            errorCode === "consent_required" ||
            errorCode === "interaction_required"
          ) {
            console.log(
              "ℹ️  Auto-SSO failed (expected) - showing public portal"
            );
            console.log("💡 User can click 'Login' button for manual SSO");

            // Silently redirect to home (public portal)
            navigate("/", { replace: true });
            return;
          }

          // Other errors - show error message
          throw new Error(
            `${errorCode}: ${errorDescription || "Authentication failed"}`
          );
        }

        // No error - process successful callback
        console.log("📥 Processing OIDC callback...");
        const user = await handleCallback();
        console.log("✅ OIDC authentication successful:", user.profile?.email);

        // Small delay to ensure state is updated
        setTimeout(() => {
          navigate("/", { replace: true });
        }, 100);
      } catch (err) {
        console.error("❌ OIDC authentication error:", err);
        setError(err instanceof Error ? err.message : "Authentication failed");
      }
    };

    processCallback();
  }, [navigate]);

  if (error) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        padding={3}
      >
        <Alert severity="error" sx={{ maxWidth: 500, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Authentication Error
          </Typography>
          <Typography variant="body2">{error}</Typography>
        </Alert>
        <Typography
          variant="body2"
          color="textSecondary"
          sx={{ cursor: "pointer", textDecoration: "underline" }}
          onClick={() => navigate("/")}
        >
          Return to Home
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
    >
      <CircularProgress size={60} sx={{ mb: 3 }} />
      <Typography variant="h6" color="textSecondary">
        Processing login...
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
        Please wait while we complete your authentication.
      </Typography>
    </Box>
  );
}

export default OidcCallback;
